import type { LatLong } from '@iheartjane/geolocation';
import type {
  Nullable,
  ShopperReservationMode,
  StoreType,
} from '@iheartjane/types';

import type { BloomMenuStore } from '@jane/bloom/types/schemas/roots/bloom/menu';
import { nearbySort } from '@jane/bloom/utils/geo/nearbySort';
import { getStoresWithDetails } from '@jane/bloom/viewModels/getStoresWithDetails';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';

interface StoreCriteria {
  currentPinnedStore: BloomMenuStore;
  deliverableStoreIds?: Nullable<string[]>;
  reservationMode: ShopperReservationMode;
  searchAddressCoords: Partial<LatLong>;
  storeType: StoreType;
}

interface FilteredStores {
  availableStores: BloomMenuStore[];
  currentStore?: StoreViewModel;
  deliveryStores: BloomMenuStore[];
  medStores: BloomMenuStore[];
  pickupStores: BloomMenuStore[];
  recStores: BloomMenuStore[];
}

export const filteredAndSortedStores = (
  stores: BloomMenuStore[],
  criteria: StoreCriteria
): FilteredStores => {
  const { currentPinnedStore, searchAddressCoords } = criteria;
  const storesWithDetails = getStoresWithDetails({
    stores,
    userLocation: searchAddressCoords,
  });

  const sortedStoresByDistance =
    searchAddressCoords?.lat && searchAddressCoords?.long
      ? nearbySort(
          {
            lat: searchAddressCoords.lat,
            long: searchAddressCoords.long,
          },
          storesWithDetails
        )
      : storesWithDetails;

  // Move pinned store to the front of the array
  sortedStoresByDistance.forEach((store, i) => {
    if (store.id === currentPinnedStore.id) {
      sortedStoresByDistance.splice(i, 1);
      sortedStoresByDistance.unshift(store);
    }
  });

  return {
    availableStores: filteredStores(sortedStoresByDistance, criteria),
    currentStore: storesWithDetails.find(
      (store) => store.id === currentPinnedStore.id
    ),
    deliveryStores: sortedStoresByDistance.filter(({ delivery }) => delivery),
    medStores: sortedStoresByDistance.filter(
      ({ recreational }) => !recreational
    ),
    // Curbside is a sub-mode of pickup — a curbside store always has pickup:true —
    // so the pickup tab already represents every pickup-or-curbside store.
    pickupStores: sortedStoresByDistance.filter(({ pickup }) => pickup),
    recStores: sortedStoresByDistance.filter(
      ({ recreational }) => recreational
    ),
  };
};

/**
 * Exported just for testing; prefer filteredAndSortedStores if possible.
 */
export const filteredStores = (
  stores: BloomMenuStore[],
  { deliverableStoreIds, reservationMode, storeType }: StoreCriteria
): BloomMenuStore[] => {
  return stores
    .filter(({ recreational }) => {
      return (
        storeType === 'all' ||
        (storeType === 'recreational' && recreational) ||
        (storeType === 'medical' && !recreational)
      );
    })
    .filter(({ delivery, id, pickup }) => {
      return (
        (reservationMode === 'pickup' && pickup) ||
        (reservationMode === 'delivery' &&
          delivery &&
          (!deliverableStoreIds || deliverableStoreIds.includes(id.toString())))
      );
    });
};
